import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  QUERY_KEY_DATES_LIST,
  type KeyDatePage,
  type KeyDatesListQuery,
  type KeyDatesListQueryVariables,
} from '@edwin-group/people-graphql';
import { useKeyDatesListStore } from '../store/useKeyDatesListStore';
import type { KeyDate } from '../../shared/types';
import { useAuthentication } from '@/hooks/useAuthentication';

const PAGE_SIZE = 50;

interface SectionType {
  documentId: string;
  value: string;
  label?: string | null;
}

interface TopicLabel {
  documentId: string;
  value: string;
  label?: string | null;
}

const getMonthDateRange = (month: Date) => {
  const startDate = new Date(month.getFullYear(), month.getMonth(), 1);
  const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const monthStr = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${monthStr}-${day}`;
  };

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
};

export const useKeyDatesList = () => {
  const { schoolContext } = useAuthentication();
  const currentMonth = useKeyDatesListStore((state) => state.currentMonth);
  const searchTerm = useKeyDatesListStore((state) => state.searchTerm);
  const sectionType = useKeyDatesListStore((state) => state.sectionType);

  const setKeyDates = useKeyDatesListStore((state) => state.setKeyDates);
  const setPagination = useKeyDatesListStore((state) => state.setPagination);
  const setLoading = useKeyDatesListStore((state) => state.setLoading);
  const setPage = useKeyDatesListStore((state) => state.setPage);

  const { startDate, endDate } = useMemo(() => getMonthDateRange(currentMonth), [currentMonth]);

  const { data: keyDatesData, loading } = useQuery<KeyDatesListQuery, KeyDatesListQueryVariables>(
    QUERY_KEY_DATES_LIST,
    {
      variables: {
        page: 1,
        pageSize: PAGE_SIZE,
        startDate,
        endDate,
        sectionType: sectionType || undefined,
        title: searchTerm || undefined,
        schoolContextDocumentId: schoolContext?.documentId ?? '',
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (keyDatesData?.keyDates) {
      const nodes = keyDatesData.keyDates.nodes || [];
      const pageInfo = keyDatesData.keyDates.pageInfo;

      const mappedKeyDates: KeyDate[] = nodes
        .filter((node): node is NonNullable<typeof node> => node !== null)
        .map((node) => ({
          documentId: node.documentId,
          title: node.title,
          description: node.description,
          dateType: node.dateType as KeyDate['dateType'],
          startDate: node.startDate,
          endDate: node.endDate,
          relativeRule: node.relativeRule,
          sectionType:
            node.sectionType && typeof node.sectionType === 'object' && 'documentId' in node.sectionType
              ? (node.sectionType as SectionType)
              : null,
          topicLabel:
            node.topicLabel && typeof node.topicLabel === 'object' && 'documentId' in node.topicLabel
              ? (node.topicLabel as TopicLabel)
              : null,
          month: node.month ?? '',
          requiresReview: node.requiresReview ?? false,
          isDeleted: node.isDeleted ?? false,
          originalText: node.originalText ?? '',
          source: node.source,
          academicYear: node.academicYear,
          bookYourPlaceUrl: node.bookYourPlaceUrl,
          learnMoreUrl: node.learnMoreUrl,
        }));

      setKeyDates(mappedKeyDates);

      if (pageInfo) {
        setPagination({
          page: pageInfo.page || 1,
          pageCount: pageInfo.pageCount || 1,
          total: pageInfo.total || 0,
          pageSize: pageInfo.pageSize || PAGE_SIZE,
        });
      }
    }
  }, [keyDatesData, setKeyDates, setPagination]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  useEffect(() => {
    if (keyDatesData?.keyDatePage) {
      setPage(keyDatesData.keyDatePage as KeyDatePage);
    }
  }, [keyDatesData, setPage]);

  return {
    loading,
  };
};
